/**
 * The full catalogue of games the platform will ship with.
 *
 * Every game is listed here from day one, even before its engine exists.
 * `implemented` is the single source of truth for what the bot and the Mini App
 * are allowed to offer, so adding a game means writing its engine and flipping
 * one flag.
 */
export const GAME_IDS = [
    // board
    'tictactoe',
    'connect4',
    'chess',
    'checkers',
    'battleship',
    'backgammon',
    'domino',
    'reversi',
    'dotsboxes',
    // cards
    'durak',
    'blackjack',
    'uno',
    // words
    'wordchain',
    'balda',
    'hangman',
    'crocodile',
    'anagram',
    // brain / speed
    'quizduel',
    'mathduel',
    'memory',
    'reaction',
    'bullscows',
    'sudoku',
    // quick
    'rps',
    'dice',
    // couple
    'howwellweknow',
    'truthordare',
    'neverhaveiever',
    'compatibility',
    'dailyquestion',
];
export const GAME_CATEGORIES = ['board', 'cards', 'words', 'brain', 'quick', 'couple'];
export const CATEGORY_LABELS = {
    board: { uz: 'Taxta o‘yinlari', emoji: '♟️' },
    cards: { uz: 'Karta o‘yinlari', emoji: '🃏' },
    words: { uz: 'So‘z o‘yinlari', emoji: '🔤' },
    brain: { uz: 'Aql va tezlik', emoji: '🧠' },
    quick: { uz: 'Tezkor', emoji: '⚡' },
    couple: { uz: 'Juftliklar uchun', emoji: '💞' },
};
export const GAMES = {
    // ---------------------------------------------------------------- board ---
    tictactoe: {
        id: 'tictactoe',
        name: 'X-O',
        emoji: '❌',
        category: 'board',
        surface: 'inline',
        turnMode: 'alternating',
        minPlayers: 2,
        maxPlayers: 2,
        turnSeconds: 45,
        tagline: 'Uchtasini bir qatorga tiz',
        implemented: true,
    },
    connect4: {
        id: 'connect4',
        name: '4 ta ketma-ket',
        emoji: '🔴',
        category: 'board',
        surface: 'inline',
        turnMode: 'alternating',
        minPlayers: 2,
        maxPlayers: 2,
        turnSeconds: 45,
        tagline: 'Ustundan tashla, to‘rttani tiz',
        implemented: true,
    },
    chess: {
        id: 'chess',
        name: 'Shaxmat',
        emoji: '♟️',
        category: 'board',
        surface: 'miniapp',
        turnMode: 'alternating',
        minPlayers: 2,
        maxPlayers: 2,
        turnSeconds: 120,
        tagline: 'Klassik shaxmat, to‘liq qoidalar bilan',
        implemented: true,
    },
    checkers: {
        id: 'checkers',
        name: 'Shashka',
        emoji: '⚪',
        category: 'board',
        surface: 'miniapp',
        turnMode: 'alternating',
        minPlayers: 2,
        maxPlayers: 2,
        turnSeconds: 60,
        tagline: 'Raqib toshlarini yeb tugat',
        implemented: true,
    },
    battleship: {
        id: 'battleship',
        name: 'Dengiz jangi',
        emoji: '🚢',
        category: 'board',
        surface: 'miniapp',
        turnMode: 'alternating',
        minPlayers: 2,
        maxPlayers: 2,
        turnSeconds: 45,
        tagline: 'Kemalarni yashir, raqibnikini topib cho‘ktir',
        implemented: true,
    },
    backgammon: {
        id: 'backgammon',
        name: 'Nardi',
        emoji: '🎲',
        category: 'board',
        surface: 'miniapp',
        turnMode: 'alternating',
        minPlayers: 2,
        maxPlayers: 2,
        turnSeconds: 60,
        tagline: 'Zar tashla, toshlarni uyga yetkaz',
        implemented: true,
    },
    domino: {
        id: 'domino',
        name: 'Domino',
        emoji: '🁣',
        category: 'board',
        surface: 'miniapp',
        turnMode: 'alternating',
        minPlayers: 2,
        maxPlayers: 2,
        turnSeconds: 45,
        tagline: 'Mos raqamni ulab, toshlaringni tugat',
        implemented: true,
    },
    reversi: {
        id: 'reversi',
        name: 'Reversi',
        emoji: '⚫',
        category: 'board',
        surface: 'miniapp',
        turnMode: 'alternating',
        minPlayers: 2,
        maxPlayers: 2,
        turnSeconds: 45,
        tagline: 'Raqib toshlarini o‘z rangingga ag‘dar',
        implemented: true,
    },
    dotsboxes: {
        id: 'dotsboxes',
        name: 'Nuqtalar va kvadratlar',
        emoji: '🔲',
        category: 'board',
        surface: 'miniapp',
        turnMode: 'alternating',
        minPlayers: 2,
        maxPlayers: 2,
        turnSeconds: 30,
        tagline: 'Chiziq torting, kvadratni yop',
        implemented: true,
    },
    // ---------------------------------------------------------------- cards ---
    durak: {
        id: 'durak',
        name: 'Durak',
        emoji: '🂡',
        category: 'cards',
        surface: 'miniapp',
        turnMode: 'alternating',
        minPlayers: 2,
        maxPlayers: 2,
        turnSeconds: 45,
        tagline: 'Hujum qil, qaytar — oxirgi qolgan yutqazadi',
        implemented: true,
    },
    blackjack: {
        id: 'blackjack',
        name: 'Blackjack',
        emoji: '🃏',
        category: 'cards',
        surface: 'miniapp',
        turnMode: 'alternating',
        minPlayers: 2,
        maxPlayers: 2,
        turnSeconds: 30,
        tagline: '21 ga yaqinlash, lekin oshirib yuborma',
        implemented: true,
    },
    uno: {
        id: 'uno',
        name: 'UNO',
        emoji: '🎴',
        category: 'cards',
        surface: 'miniapp',
        turnMode: 'alternating',
        minPlayers: 2,
        maxPlayers: 2,
        turnSeconds: 30,
        tagline: 'Rang yoki raqamga mos tashla, kartangni tugat',
        implemented: true,
    },
    // ---------------------------------------------------------------- words ---
    wordchain: {
        id: 'wordchain',
        name: 'So‘z zanjiri',
        emoji: '🔗',
        category: 'words',
        surface: 'inline',
        turnMode: 'alternating',
        minPlayers: 2,
        maxPlayers: 2,
        turnSeconds: 45,
        tagline: 'Oxirgi harfdan yangi so‘z boshla',
        implemented: true,
    },
    balda: {
        id: 'balda',
        name: 'Balda',
        emoji: '🔡',
        category: 'words',
        surface: 'miniapp',
        turnMode: 'alternating',
        minPlayers: 2,
        maxPlayers: 2,
        turnSeconds: 90,
        tagline: 'Harf qo‘y, so‘z tuz, ochko yig‘',
        implemented: true,
    },
    hangman: {
        id: 'hangman',
        name: 'Osilgan odam',
        emoji: '🪢',
        category: 'words',
        surface: 'inline',
        turnMode: 'alternating',
        minPlayers: 2,
        maxPlayers: 2,
        turnSeconds: 45,
        tagline: 'Birgalikda so‘zni harflab toping',
        implemented: true,
    },
    crocodile: {
        id: 'crocodile',
        name: 'Krokodil',
        emoji: '🐊',
        category: 'words',
        surface: 'inline',
        turnMode: 'alternating',
        minPlayers: 2,
        maxPlayers: 2,
        turnSeconds: 90,
        tagline: 'So‘zni aytmasdan tushuntir, raqib topsin',
        implemented: true,
    },
    anagram: {
        id: 'anagram',
        name: 'Anagramma poygasi',
        emoji: '🔀',
        category: 'words',
        surface: 'miniapp',
        turnMode: 'simultaneous',
        minPlayers: 2,
        maxPlayers: 2,
        turnSeconds: 60,
        tagline: 'Aralashgan harflardan asl so‘zni kim tez topadi',
        implemented: true,
    },
    // ---------------------------------------------------------------- brain ---
    quizduel: {
        id: 'quizduel',
        name: 'Viktorina dueli',
        emoji: '❓',
        category: 'brain',
        surface: 'miniapp',
        turnMode: 'simultaneous',
        minPlayers: 2,
        maxPlayers: 2,
        turnSeconds: 20,
        tagline: 'Bir xil savollar — kim ko‘p va tez biladi',
        implemented: true,
    },
    mathduel: {
        id: 'mathduel',
        name: 'Matematika dueli',
        emoji: '➗',
        category: 'brain',
        surface: 'miniapp',
        turnMode: 'simultaneous',
        minPlayers: 2,
        maxPlayers: 2,
        turnSeconds: 15,
        tagline: 'Tez hisobla — kim oldin to‘g‘ri javob beradi',
        implemented: true,
    },
    memory: {
        id: 'memory',
        name: 'Xotira',
        emoji: '🧩',
        category: 'brain',
        surface: 'miniapp',
        turnMode: 'alternating',
        minPlayers: 2,
        maxPlayers: 2,
        turnSeconds: 30,
        tagline: 'Juft kartalarni eslab qol va top',
        implemented: true,
    },
    reaction: {
        id: 'reaction',
        name: 'Reaksiya tezligi',
        emoji: '⚡',
        category: 'brain',
        surface: 'miniapp',
        turnMode: 'realtime',
        minPlayers: 2,
        maxPlayers: 2,
        turnSeconds: 10,
        tagline: 'Signal chiqishi bilan kim tezroq bosadi',
        implemented: true,
    },
    bullscows: {
        id: 'bullscows',
        name: 'Raqam topish',
        emoji: '🔢',
        category: 'brain',
        surface: 'inline',
        turnMode: 'alternating',
        minPlayers: 2,
        maxPlayers: 2,
        turnSeconds: 60,
        tagline: 'Raqibning 4 xonali sonini kim oldin topadi',
        implemented: true,
    },
    sudoku: {
        id: 'sudoku',
        name: 'Sudoku poygasi',
        emoji: '9️⃣',
        category: 'brain',
        surface: 'miniapp',
        turnMode: 'simultaneous',
        minPlayers: 2,
        maxPlayers: 2,
        turnSeconds: 600,
        tagline: 'Bir xil sudoku — kim tez yechadi',
        implemented: true,
    },
    // ---------------------------------------------------------------- quick ---
    rps: {
        id: 'rps',
        name: 'Tosh-qaychi-qog‘oz',
        emoji: '✂️',
        category: 'quick',
        surface: 'inline',
        turnMode: 'simultaneous',
        minPlayers: 2,
        maxPlayers: 2,
        turnSeconds: 20,
        tagline: 'Klassik — 3 yoki 5 raundgacha',
        implemented: true,
    },
    dice: {
        id: 'dice',
        name: 'Zar',
        emoji: '🎲',
        category: 'quick',
        surface: 'inline',
        turnMode: 'alternating',
        minPlayers: 2,
        maxPlayers: 2,
        turnSeconds: 20,
        tagline: 'Tavakkal qil — yana tashla yoki saqlab qol',
        implemented: true,
    },
    // --------------------------------------------------------------- couple ---
    howwellweknow: {
        id: 'howwellweknow',
        name: 'Bir-birimizni bilamizmi?',
        emoji: '💘',
        category: 'couple',
        surface: 'inline',
        turnMode: 'simultaneous',
        minPlayers: 2,
        maxPlayers: 2,
        turnSeconds: 60,
        tagline: 'Ikkalangiz javob bering — mos kelsa ochko',
        implemented: true,
        cooperative: true,
    },
    truthordare: {
        id: 'truthordare',
        name: 'Rostini ayt yoki jur’at',
        emoji: '🎯',
        category: 'couple',
        surface: 'inline',
        turnMode: 'alternating',
        minPlayers: 2,
        maxPlayers: 2,
        turnSeconds: 120,
        tagline: 'Savolga rost javob ber yoki topshiriqni bajar',
        implemented: true,
        cooperative: true,
    },
    neverhaveiever: {
        id: 'neverhaveiever',
        name: 'Men hech qachon...',
        emoji: '🙊',
        category: 'couple',
        surface: 'inline',
        turnMode: 'simultaneous',
        minPlayers: 2,
        maxPlayers: 2,
        turnSeconds: 60,
        tagline: 'Qilganmisan yoki yo‘q — rostini ayt',
        implemented: true,
        cooperative: true,
    },
    compatibility: {
        id: 'compatibility',
        name: 'Mos kelish testi',
        emoji: '💯',
        category: 'couple',
        surface: 'inline',
        turnMode: 'simultaneous',
        minPlayers: 2,
        maxPlayers: 2,
        turnSeconds: 60,
        tagline: 'Javoblaringiz necha foizga mos keladi?',
        implemented: true,
        cooperative: true,
    },
    dailyquestion: {
        id: 'dailyquestion',
        name: 'Kunlik savol',
        emoji: '📅',
        category: 'couple',
        surface: 'inline',
        turnMode: 'simultaneous',
        minPlayers: 2,
        maxPlayers: 2,
        turnSeconds: 86400,
        tagline: 'Har kuni bitta savol — javoblarni keyin ko‘rasiz',
        implemented: true,
        cooperative: true,
    },
};
export const ALL_GAMES = GAME_IDS.map((id) => GAMES[id]);
export function isGameId(value) {
    return GAME_IDS.includes(value);
}
export function implementedGames() {
    return ALL_GAMES.filter((g) => g.implemented);
}
export function gamesByCategory(category) {
    return ALL_GAMES.filter((g) => g.category === category);
}
/**
 * The games the Mini App can draw a board for.
 *
 * Not the same question as `surface`. `surface` says where a game is meant to
 * be played; this says where it *can* be. Most Mini App games are only playable
 * there, but an inline game may also have a board — X-O does — and a Mini App
 * board may be written before the catalogue is updated.
 *
 * The bot reads this to decide whether to offer the "open the app" button. It
 * matters because offering it for a game with no board is a dead end: the app
 * opens, says the board is not drawn yet, and the player has to find their way
 * back on their own.
 *
 * Kept in step with the Mini App's own registry by a test that compares the
 * two, so a board added on one side and forgotten on the other fails loudly
 * rather than quietly showing — or hiding — the wrong button.
 */
export const MINIAPP_BOARDS = [
    'tictactoe',
    'chess',
    'checkers',
    'battleship',
    'backgammon',
    'domino',
    'reversi',
    'dotsboxes',
    'durak',
    'blackjack',
    'uno',
    'balda',
    'anagram',
    'quizduel',
    'mathduel',
    'memory',
    'reaction',
    'sudoku',
];
const MINIAPP_BOARD_SET = new Set(MINIAPP_BOARDS);
export function hasMiniAppBoard(id) {
    return MINIAPP_BOARD_SET.has(id);
}
//# sourceMappingURL=games.js.map